import React, { useState, useEffect } from 'react';
import { coursesApi, questionsApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, MagnifyingGlass, Pencil, Trash, Question as QuestionIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import ProcessGuide from '../../components/ProcessGuide';

export default function QuestionsPage() {
  const [questions, setQuestions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // single question
  const [deleteDeps, setDeleteDeps] = useState(null);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeps, setBulkDeps] = useState(null);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const [formData, setFormData] = useState({
    question_text: '', question_type: 'multiple_choice', course_id: '',
    correct_answer: '', explanation: '', question_group: '',
    options: [{ id: uuidv4(), text: '' }, { id: uuidv4(), text: '' }, { id: uuidv4(), text: '' }, { id: uuidv4(), text: '' }]
  });
  const [groupFilter, setGroupFilter] = useState('all');
  const [formMandante, setFormMandante] = useState('all');
  const [mandanteFilter, setMandanteFilter] = useState('all');

  const mandantes = [...new Map(courses.filter(c => c.client_id).map(c => [c.client_id, c.client_name || 'Sin mandante'])).entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [questionsRes, coursesRes] = await Promise.all([questionsApi.getAll(), coursesApi.getAll()]);
      setQuestions(questionsRes.data);
      setCourses(coursesRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        question_group: formData.question_group?.trim() || null,
        options: formData.question_type === 'true_false'
          ? [{ id: '1', text: 'Verdadero' }, { id: '2', text: 'Falso' }]
          : formData.options.filter(o => o.text.trim())
      };
      if (editingQuestion) {
        await questionsApi.update(editingQuestion.id, data);
        toast.success('Pregunta actualizada');
      } else {
        await questionsApi.create(data);
        toast.success('Pregunta creada');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({
      question_text: '', question_type: 'multiple_choice', course_id: '',
      correct_answer: '', explanation: '', question_group: '',
      options: [{ id: uuidv4(), text: '' }, { id: uuidv4(), text: '' }, { id: uuidv4(), text: '' }, { id: uuidv4(), text: '' }]
    });
    setEditingQuestion(null);
    setFormMandante('all');
  };

  const openEditDialog = (question) => {
    setEditingQuestion(question);
    const course = courses.find(c => c.id === question.course_id);
    setFormMandante(course?.client_id || 'all');
    setFormData({
      question_text: question.question_text, question_type: question.question_type,
      course_id: question.course_id, correct_answer: question.correct_answer,
      explanation: question.explanation || '', question_group: question.question_group || '',
      options: question.options || [{ id: uuidv4(), text: '' }, { id: uuidv4(), text: '' }, { id: uuidv4(), text: '' }, { id: uuidv4(), text: '' }]
    });
    setIsDialogOpen(true);
  };

  const updateOption = (index, text) => {
    const newOptions = [...formData.options];
    newOptions[index].text = text;
    setFormData({ ...formData, options: newOptions });
  };

  // Delete single
  const handleDeleteSingle = async (question) => {
    setDeleteTarget(question);
    setLoadingDeps(true);
    try {
      const res = await questionsApi.getDependencies(question.id);
      setDeleteDeps(res.data);
    } catch {
      setDeleteDeps({ tests: [], tests_count: 0, has_dependencies: false });
    } finally { setLoadingDeps(false); }
  };

  const confirmDeleteSingle = async () => {
    if (!deleteTarget) return;
    try {
      await questionsApi.delete(deleteTarget.id);
      toast.success('Pregunta eliminada');
      setSelectedIds(prev => prev.filter(id => id !== deleteTarget.id));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally { setDeleteTarget(null); setDeleteDeps(null); }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    setShowBulkDelete(true);
    setLoadingDeps(true);
    try {
      const res = await questionsApi.bulkDependencies(selectedIds);
      setBulkDeps(res.data);
    } catch {
      setBulkDeps({ total: selectedIds.length, tests: [], tests_count: 0, has_dependencies: false });
    } finally { setLoadingDeps(false); }
  };

  const confirmBulkDelete = async () => {
    try {
      const res = await questionsApi.bulkDelete(selectedIds);
      toast.success(res.data.message);
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally { setShowBulkDelete(false); setBulkDeps(null); }
  };

  // Selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const visibleIds = filteredQuestions.map(q => q.id);
    const allSelected = visibleIds.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? selectedIds.filter(id => !visibleIds.includes(id)) : [...new Set([...selectedIds, ...visibleIds])]);
  };

  const availableGroups = [...new Set(questions.filter(q => (courseFilter === 'all' || q.course_id === courseFilter) && q.question_group).map(q => q.question_group))].sort();

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = q.question_text.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCourse = courseFilter === 'all' || q.course_id === courseFilter;
    const matchesGroup = groupFilter === 'all' || (groupFilter === '__none__' ? !q.question_group : q.question_group === groupFilter);
    const course = courses.find(c => c.id === q.course_id);
    const matchesMandante = mandanteFilter === 'all' || course?.client_id === mandanteFilter;
    return matchesSearch && matchesCourse && matchesGroup && matchesMandante;
  });

  const getCourseName = (courseId) => courses.find(c => c.id === courseId)?.name || 'Sin curso';
  const allVisibleSelected = filteredQuestions.length > 0 && filteredQuestions.every(q => selectedIds.includes(q.id));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="questions-page">
      <ProcessGuide activeStep={1} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Banco de Preguntas</h1>
          <p className="text-[#2B4B8C]/60">Gestiona las preguntas para evaluaciones</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button variant="outline" onClick={handleBulkDelete} className="border-red-300 text-red-500 hover:bg-red-50" data-testid="bulk-delete-questions">
              <Trash size={16} className="mr-2" /> Eliminar ({selectedIds.length})
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="create-question-btn">
                <Plus className="mr-2" size={20} /> Nueva Pregunta
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-[#2B4B8C]">{editingQuestion ? 'Editar Pregunta' : 'Nueva Pregunta'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Pregunta</Label>
                  <Textarea value={formData.question_text} onChange={(e) => setFormData({ ...formData, question_text: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" rows={3} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#2B4B8C]/80">Tipo</Label>
                    <Select value={formData.question_type} onValueChange={(v) => setFormData({ ...formData, question_type: v, correct_answer: v === 'true_false' ? '' : formData.correct_answer })}>
                      <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-[#2B4B8C]/10">
                        <SelectItem value="multiple_choice">Selección Múltiple</SelectItem>
                        <SelectItem value="true_false">Verdadero/Falso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#2B4B8C]/80">Mandante</Label>
                    <Select value={formMandante} onValueChange={(v) => {
                      setFormMandante(v);
                      const currentCourse = courses.find(c => c.id === formData.course_id);
                      if (v !== 'all' && currentCourse && currentCourse.client_id !== v) {
                        setFormData(fd => ({ ...fd, course_id: '' }));
                      }
                    }}>
                      <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="question-mandante-filter"><SelectValue placeholder="Todos los mandantes" /></SelectTrigger>
                      <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                        <SelectItem value="all">Todos los mandantes</SelectItem>
                        {mandantes.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Curso</Label>
                  <Select value={formData.course_id} onValueChange={(v) => setFormData({ ...formData, course_id: v })}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"><SelectValue placeholder="Seleccionar curso" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                      {courses.filter(c => formMandante === 'all' || c.client_id === formMandante).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Grupo / Batería de preguntas (opcional)</Label>
                  <Input
                    list="question-groups-list"
                    value={formData.question_group}
                    onChange={(e) => setFormData({ ...formData, question_group: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    placeholder="Ej: Grupo A, Batería 1... (deje vacío para sin grupo)"
                    data-testid="question-group-input"
                  />
                  <datalist id="question-groups-list">
                    {[...new Set(questions.filter(q => !formData.course_id || q.course_id === formData.course_id).map(q => q.question_group).filter(Boolean))].map(g => (
                      <option key={g} value={g} />
                    ))}
                  </datalist>
                  <p className="text-xs text-[#2B4B8C]/50">Agrupe preguntas del curso en baterías. Las pruebas seleccionan preguntas al azar, por lo que cada intento es distinto.</p>
                </div>
                {formData.question_type === 'multiple_choice' && (
                  <div className="space-y-3">
                    <Label className="text-[#2B4B8C]/80">Opciones de Respuesta</Label>
                    {formData.options.map((option, index) => (
                      <div key={option.id} className="flex items-center gap-3">
                        <span className="text-[#2B4B8C]/60 w-6">{String.fromCharCode(65 + index)}.</span>
                        <Input value={option.text} onChange={(e) => updateOption(index, e.target.value)} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C] flex-1" placeholder={`Opción ${String.fromCharCode(65 + index)}`} />
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Respuesta Correcta</Label>
                  {formData.question_type === 'true_false' ? (
                    <Select value={formData.correct_answer} onValueChange={(v) => setFormData({ ...formData, correct_answer: v })}>
                      <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent className="bg-white border-[#2B4B8C]/10">
                        <SelectItem value="1">Verdadero</SelectItem>
                        <SelectItem value="2">Falso</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={formData.correct_answer} onValueChange={(v) => setFormData({ ...formData, correct_answer: v })}>
                      <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"><SelectValue placeholder="Seleccionar opción correcta" /></SelectTrigger>
                      <SelectContent className="bg-white border-[#2B4B8C]/10">
                        {formData.options.map((option, index) => option.text && (
                          <SelectItem key={option.id} value={option.id}>{String.fromCharCode(65 + index)}. {option.text}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Explicación (opcional)</Label>
                  <Textarea value={formData.explanation} onChange={(e) => setFormData({ ...formData, explanation: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" rows={2} placeholder="Explicación de la respuesta correcta" />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 border-[#2B4B8C]/10">Cancelar</Button>
                  <Button type="submit" className="flex-1 bg-[#2BBAA4] text-black hover:bg-[#239986]">{editingQuestion ? 'Actualizar' : 'Crear'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={20} />
              <Input placeholder="Buscar pregunta..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
            </div>
            <Select value={mandanteFilter} onValueChange={(v) => { setMandanteFilter(v); setCourseFilter('all'); setGroupFilter('all'); }}>
              <SelectTrigger className="w-full sm:w-48 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="mandante-filter"><SelectValue placeholder="Filtrar por mandante" /></SelectTrigger>
              <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                <SelectItem value="all">Todos los mandantes</SelectItem>
                {mandantes.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={courseFilter} onValueChange={(v) => { setCourseFilter(v); setGroupFilter('all'); }}>
              <SelectTrigger className="w-full sm:w-48 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"><SelectValue placeholder="Filtrar por curso" /></SelectTrigger>
              <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                <SelectItem value="all">Todos los cursos</SelectItem>
                {courses.filter(c => mandanteFilter === 'all' || c.client_id === mandanteFilter).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={groupFilter} onValueChange={setGroupFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="group-filter"><SelectValue placeholder="Filtrar por grupo" /></SelectTrigger>
              <SelectContent className="bg-white border-[#2B4B8C]/10">
                <SelectItem value="all">Todos los grupos</SelectItem>
                {availableGroups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                <SelectItem value="__none__">Sin grupo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Select all */}
      {filteredQuestions.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-questions" />
          <span className="text-sm text-[#2B4B8C]/60">Seleccionar todas ({filteredQuestions.length})</span>
        </div>
      )}

      {/* Questions List */}
      <div className="grid gap-4">
        {filteredQuestions.map((question) => (
          <Card key={question.id} className={`bg-white border-[#2B4B8C]/10 hover:border-[#2BBAA4]/30 transition-colors ${selectedIds.includes(question.id) ? 'ring-2 ring-[#2BBAA4]/40' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Checkbox
                  checked={selectedIds.includes(question.id)}
                  onCheckedChange={() => toggleSelect(question.id)}
                  className="mt-1"
                  data-testid={`select-q-${question.id}`}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <QuestionIcon size={20} className="text-[#2BBAA4]" />
                    <Badge className={question.question_type === 'multiple_choice' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20 border' : 'bg-purple-500/10 text-purple-400 border-purple-500/20 border'}>
                      {question.question_type === 'multiple_choice' ? 'Selección Múltiple' : 'V/F'}
                    </Badge>
                    <Badge className="bg-gray-500/10 text-[#2B4B8C]/60 border-gray-500/20 border">{getCourseName(question.course_id)}</Badge>
                    {question.question_group ? (
                      <Badge className="bg-[#2BBAA4]/10 text-[#2BBAA4] border-[#2BBAA4]/30 border" data-testid="question-group-badge">{question.question_group}</Badge>
                    ) : null}
                  </div>
                  <p className="text-[#2B4B8C] font-medium">{question.question_text}</p>
                  {question.options && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {question.options.map((opt, i) => (
                        <div key={opt.id} className={`text-sm px-3 py-2 rounded ${opt.id === question.correct_answer ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-[#f8fafc] text-[#2B4B8C]/60'}`}>
                          {String.fromCharCode(65 + i)}. {opt.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(question)} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C]" data-testid={`edit-q-${question.id}`}>
                    <Pencil size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteSingle(question)} className="text-red-400 hover:text-red-500" data-testid={`delete-q-${question.id}`}>
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filteredQuestions.length === 0 && (
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="py-12 text-center text-[#2B4B8C]/50">No se encontraron preguntas</CardContent>
          </Card>
        )}
      </div>

      {/* Single Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteDeps(null); } }}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
          <DialogHeader><DialogTitle className="text-[#2B4B8C]">Eliminar Pregunta</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="delete-question-dialog">
            <p className="text-[#2B4B8C]/70 text-sm">
              ¿Está seguro de eliminar la pregunta: <span className="font-semibold text-[#2B4B8C]">«{deleteTarget?.question_text?.substring(0, 80)}...»</span>?
            </p>
            {loadingDeps ? (
              <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2BBAA4]" /></div>
            ) : deleteDeps?.has_dependencies ? (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 space-y-2">
                <p className="text-sm font-medium text-red-600">Esta pregunta está vinculada a:</p>
                <p className="text-sm text-red-600">{deleteDeps.tests_count} test(s): <span className="font-medium">{deleteDeps.tests.join(', ')}</span></p>
                <p className="text-xs text-red-500/70">Se removerá de los tests automáticamente.</p>
              </div>
            ) : (
              <p className="text-sm text-[#2B4B8C]/50">Esta pregunta no tiene vínculos activos.</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteDeps(null); }} className="flex-1 border-[#2B4B8C]/10">Cancelar</Button>
              <Button onClick={confirmDeleteSingle} className="flex-1 bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-question">Eliminar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDelete} onOpenChange={(open) => { if (!open) { setShowBulkDelete(false); setBulkDeps(null); } }}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
          <DialogHeader><DialogTitle className="text-[#2B4B8C]">Eliminar Preguntas</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="bulk-delete-dialog">
            <p className="text-[#2B4B8C]/70">¿Está seguro de eliminar <span className="font-semibold text-[#2B4B8C]">{selectedIds.length} pregunta(s)</span>?</p>
            {loadingDeps ? (
              <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2BBAA4]" /></div>
            ) : bulkDeps?.has_dependencies ? (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 space-y-2">
                <p className="text-sm font-medium text-red-600">Algunas preguntas están vinculadas a tests:</p>
                <p className="text-sm text-red-600">{bulkDeps.tests_count} test(s): <span className="font-medium">{bulkDeps.tests.join(', ')}</span></p>
                <p className="text-xs text-red-500/70">Se removerán de los tests automáticamente.</p>
              </div>
            ) : (
              <p className="text-sm text-[#2B4B8C]/50">Las preguntas seleccionadas no tienen vínculos activos.</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setShowBulkDelete(false); setBulkDeps(null); }} className="flex-1 border-[#2B4B8C]/10">Cancelar</Button>
              <Button onClick={confirmBulkDelete} className="flex-1 bg-red-500 text-white hover:bg-red-600" data-testid="confirm-bulk-delete">Eliminar {selectedIds.length}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
